import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      await fetchOrganizations();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/organizations`);
      setOrganizations(response.data);
      if (response.data.length > 0) {
        const savedOrgId = localStorage.getItem('currentOrgId');
        const org = response.data.find(o => o.id === savedOrgId) || response.data[0];
        setCurrentOrg(org);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const login = async (email, password, selectedOrgId = null) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    await fetchOrganizations();
    
    // If org was selected on login, switch to it
    if (selectedOrgId) {
      localStorage.setItem('currentOrgId', selectedOrgId);
    }
    
    return userData;
  };

  const register = async (email, password, name, role = 'viewer') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    await fetchOrganizations();
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentOrgId');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setOrganizations([]);
    setCurrentOrg(null);
  };

  const switchOrganization = (org) => {
    setCurrentOrg(org);
    localStorage.setItem('currentOrgId', org.id);
  };

  const hasPermission = (requiredRoles) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  const canEdit = () => hasPermission(['super_admin', 'admin', 'accountant', 'cashier']);
  const canAdmin = () => hasPermission(['super_admin', 'admin']);
  const isSuperAdmin = () => hasPermission(['super_admin']);
  const isCashier = () => hasPermission(['cashier']);
  
  // Check if current organization is VAT registered
  const isVatRegistered = () => {
    if (!currentOrg) return true; // Default to true if no org
    return currentOrg.vat_registered !== false; // Default to true if not set
  };

  /**
   * Check if user has a specific action permission for a module.
   * @param {string} modulePath - The module path (e.g., '/inventory', '/customers')
   * @param {string} action - The action to check ('view', 'add', 'edit', 'delete', 'print', 'ai')
   * @returns {boolean} - True if user has permission
   */
  const checkActionPermission = (modulePath, action) => {
    // Super admin always has full access
    if (isSuperAdmin()) return true;
    
    // Check dynamic role permissions from org settings
    const rolePerms = currentOrg?.role_permissions;
    if (rolePerms && user?.role && rolePerms[user.role]) {
      const userRolePerms = rolePerms[user.role];
      
      // Handle new action-level format: { modulePath: ['view', 'add', ...] }
      if (typeof userRolePerms === 'object' && !Array.isArray(userRolePerms)) {
        const modulePerms = userRolePerms[modulePath];
        return Array.isArray(modulePerms) && modulePerms.includes(action);
      }
      
      // Handle legacy array format: ['/path1', '/path2', ...]
      // Legacy format grants all actions if module is accessible
      if (Array.isArray(userRolePerms)) {
        return userRolePerms.includes(modulePath);
      }
    }
    
    // Fallback: allow for traditional roles if no permissions configured
    if (!rolePerms || Object.keys(rolePerms).length === 0) {
      if (action === 'view') return true;
      if (['add', 'edit'].includes(action)) return hasPermission(['super_admin', 'admin', 'accountant']);
      if (action === 'delete') return hasPermission(['super_admin', 'admin']);
      if (action === 'print') return true;
      return false;
    }
    
    return false;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      organizations,
      currentOrg,
      login,
      register,
      logout,
      switchOrganization,
      hasPermission,
      canEdit,
      canAdmin,
      isSuperAdmin,
      isCashier,
      isVatRegistered,
      checkActionPermission,
      fetchOrganizations
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
